import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { pageStateQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  mergeMarketplaceSearch,
  toMarketplaceSearch,
  type MarketplaceSearch,
  type MarketplaceTab,
} from "@/features/marketplace/lib/marketplace-search";
import { MarketplacePage } from "@/features/marketplace/pages/MarketplacePage";

export type { MarketplaceSearch, MarketplaceTab };

function MarketplaceRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("marketplace.title", "Marketplace"));

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { isLoading, error } = usePageState("marketplace");

  if (isLoading) return null;
  if (error) return <PageError is403={error instanceof PageAccessError} />;

  const tab = search.tab ?? "vendors";

  return (
    <MarketplacePage
      search={{ ...search, tab }}
      onSearchChange={(patch) =>
        navigate({
          search: (prev) => mergeMarketplaceSearch(prev, patch),
          replace: true,
        })
      }
    />
  );
}

export const Route = createFileRoute("/_authenticated/marketplace/")({
  beforeLoad: () => assertPagePermission("marketplace"),
  validateSearch: toMarketplaceSearch,
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData(pageStateQueryOptions("marketplace"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      }),
  component: MarketplaceRoute,
  errorComponent: PageAccessErrorComponent,
});
